/** Mirrors backend TimeSheetStatus : short */
export const TimeSheetStatus = {
  Draft: 0,
  Submitted: 1,
  Approved: 2,
  Rejected: 3,
  Returned: 4,
};

export const TIME_SHEET_STATUS_LABEL = {
  [TimeSheetStatus.Draft]: "Draft",
  [TimeSheetStatus.Submitted]: "Submitted",
  [TimeSheetStatus.Approved]: "Approved",
  [TimeSheetStatus.Rejected]: "Rejected",
  [TimeSheetStatus.Returned]: "Returned",
};

export const getTimeSheetStatusLabel = (status) =>
  TIME_SHEET_STATUS_LABEL[status] ?? "—";

/**
 * Mirrors backend `TimeSheetApprovalStatus` (submitted timesheet workflow).
 * C#: Pending, Approved, Rejected, Returned → 0..3
 */
export const TimeSheetApprovalStatus = {
  Pending: 0,
  Approved: 1,
  Rejected: 2,
  Returned: 3,
};

/** Normalize API value (number, numeric string, or name) to TimeSheetApprovalStatus or null */
export const normalizeTimeSheetApprovalStatus = (raw) => {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number" && !Number.isNaN(raw)) {
    const n = Number(raw);
    if (n >= 0 && n <= 3) return n;
    return null;
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (trimmed === "") return null;
    const asNum = Number(trimmed);
    if (Number.isInteger(asNum) && asNum >= 0 && asNum <= 3) return asNum;
    const s = trimmed.toLowerCase();
    const byName = {
      none: null,
      pending: TimeSheetApprovalStatus.Pending,
      approved: TimeSheetApprovalStatus.Approved,
      rejected: TimeSheetApprovalStatus.Rejected,
      returned: TimeSheetApprovalStatus.Returned,
    };
    return byName[s] ?? null;
  }
  return null;
};

/**
 * Badge color for queue/detail when timesheet is still `Submitted`: derive from approval status.
 */
export const approvalStatusToBadgeTimesheetStatus = (approvalStatus) => {
  switch (approvalStatus) {
    case TimeSheetApprovalStatus.Approved:
      return TimeSheetStatus.Approved;
    case TimeSheetApprovalStatus.Rejected:
      return TimeSheetStatus.Rejected;
    case TimeSheetApprovalStatus.Returned:
      return TimeSheetStatus.Returned;
    case TimeSheetApprovalStatus.Pending:
    default:
      return TimeSheetStatus.Submitted;
  }
};

/**
 * Manager/approver queue: status column reflects workflow only (`approvalStatus`), not timesheet document status.
 * @param {{ approvalStatus?: number | null }} row
 * @param {(key: string) => string} t
 */
export const getApprovalQueueRowStatusLabel = (row, t) => {
  const a = row.approvalStatus ?? TimeSheetApprovalStatus.Pending;
  switch (a) {
    case TimeSheetApprovalStatus.Approved:
      return getTimeSheetStatusLabel(TimeSheetStatus.Approved);
    case TimeSheetApprovalStatus.Rejected:
      return getTimeSheetStatusLabel(TimeSheetStatus.Rejected);
    case TimeSheetApprovalStatus.Returned:
      return getTimeSheetStatusLabel(TimeSheetStatus.Returned);
    case TimeSheetApprovalStatus.Pending:
    default:
      return t("app.timesheets.approvals.statusPending");
  }
};

/** @param {{ approvalStatus?: number | null }} row */
export const getApprovalQueueRowBadgeStatus = (row) =>
  approvalStatusToBadgeTimesheetStatus(row.approvalStatus ?? TimeSheetApprovalStatus.Pending);
